import type { Locale, MenuResponse, Money, OrderType, Product } from '@momonos/shared'
import { computeOrderTotals } from '@momonos/shared'

/**
 * The kiosk session — one customer, from the attract screen to the receipt.
 *
 * Kept as a pure reducer so the rules that decide what a customer is shown and
 * charged can be tested without a browser. Prices here are a preview only; the
 * server recomputes everything on submit (FR-ORD-2).
 */

export type KioskScreen =
  | 'attract'
  | 'order-type'
  | 'menu'
  | 'customize'
  | 'cart'
  | 'payment'
  | 'success'

export interface KioskLine {
  key: string
  product: Product
  variantId: string | null
  modifierIds: string[]
  quantity: number
  note: string | null
}

export interface KioskSession {
  screen: KioskScreen
  locale: Locale
  orderType: OrderType
  lines: KioskLine[]
  couponCode: string | null
  /** The item being configured on the customize screen. */
  customizing: {
    product: Product
    variantId: string | null
    modifierIds: string[]
    quantity: number
    note: string | null
  } | null
  /** Set once the order is placed, for the success screen. */
  placedOrder: { orderNumber: string; statusToken: string; estimatedWaitSeconds: number } | null
  /** Last interaction, for the idle reset (FR-KIO-1). */
  lastActivityAt: number
}

export function newSession(now = Date.now()): KioskSession {
  return {
    screen: 'attract',
    locale: 'en',
    orderType: 'dine_in',
    lines: [],
    couponCode: null,
    customizing: null,
    placedOrder: null,
    lastActivityAt: now,
  }
}

export type KioskAction =
  | { type: 'start'; locale: Locale }
  | { type: 'set-locale'; locale: Locale }
  | { type: 'set-order-type'; orderType: OrderType }
  | { type: 'go'; screen: KioskScreen }
  | { type: 'begin-customize'; product: Product }
  | { type: 'set-variant'; variantId: string | null }
  | {
      type: 'toggle-modifier'
      modifierId: string
      selection: 'single' | 'multi'
      groupOptionIds: string[]
    }
  | { type: 'set-customize-quantity'; quantity: number }
  | { type: 'set-customize-note'; note: string | null }
  | { type: 'confirm-customize' }
  | { type: 'cancel-customize' }
  | { type: 'set-quantity'; key: string; quantity: number }
  | { type: 'remove-line'; key: string }
  | { type: 'apply-coupon'; code: string | null }
  | { type: 'order-placed'; orderNumber: string; statusToken: string; estimatedWaitSeconds: number }
  | { type: 'reset' }

const MAX_QUANTITY = 99

const keyFor = (productId: string, variantId: string | null, modifierIds: string[]) =>
  `${productId}:${variantId ?? ''}:${[...modifierIds].sort().join(',')}`

export function sessionReducer(
  state: KioskSession,
  action: KioskAction,
  now = Date.now(),
): KioskSession {
  // Every action is an interaction, so the idle timer restarts on all of them.
  const touched = { ...state, lastActivityAt: now }

  switch (action.type) {
    case 'start':
      return { ...touched, screen: 'order-type', locale: action.locale }

    case 'set-locale':
      return { ...touched, locale: action.locale }

    case 'set-order-type':
      return { ...touched, orderType: action.orderType, screen: 'menu' }

    case 'go':
      return { ...touched, screen: action.screen }

    case 'begin-customize': {
      // Pre-select the default variant and any default modifiers, so a customer
      // who just wants the standard item can press Add immediately.
      const variantId = action.product.variants.find((variant) => variant.isDefault)?.id ?? null
      const modifierIds = action.product.modifierGroups
        .flatMap((group) => group.options)
        .filter((option) => option.isDefault)
        .map((option) => option.id)

      return {
        ...touched,
        screen: 'customize',
        customizing: { product: action.product, variantId, modifierIds, quantity: 1, note: null },
      }
    }

    case 'set-variant':
      if (!touched.customizing) return touched
      return { ...touched, customizing: { ...touched.customizing, variantId: action.variantId } }

    case 'toggle-modifier': {
      if (!touched.customizing) return touched
      const current = touched.customizing.modifierIds

      const next =
        action.selection === 'single'
          ? // Radio behaviour: choosing one clears the others in its group.
            [...current.filter((id) => !action.groupOptionIds.includes(id)), action.modifierId]
          : current.includes(action.modifierId)
            ? current.filter((id) => id !== action.modifierId)
            : [...current, action.modifierId]

      return { ...touched, customizing: { ...touched.customizing, modifierIds: next } }
    }

    case 'set-customize-quantity': {
      if (!touched.customizing) return touched
      const quantity = Math.max(1, Math.min(MAX_QUANTITY, action.quantity))
      return { ...touched, customizing: { ...touched.customizing, quantity } }
    }

    case 'set-customize-note':
      if (!touched.customizing) return touched
      return { ...touched, customizing: { ...touched.customizing, note: action.note } }

    case 'confirm-customize': {
      const item = touched.customizing
      if (!item) return touched

      const key = keyFor(item.product.id, item.variantId, item.modifierIds)
      const existing = touched.lines.findIndex((line) => line.key === key)

      const lines =
        existing >= 0
          ? touched.lines.map((line, index) =>
              index === existing
                ? { ...line, quantity: Math.min(line.quantity + item.quantity, MAX_QUANTITY) }
                : line,
            )
          : [
              ...touched.lines,
              {
                key,
                product: item.product,
                variantId: item.variantId,
                modifierIds: item.modifierIds,
                quantity: item.quantity,
                note: item.note,
              },
            ]

      return { ...touched, lines, customizing: null, screen: 'menu' }
    }

    case 'cancel-customize':
      return { ...touched, customizing: null, screen: 'menu' }

    case 'set-quantity': {
      if (action.quantity <= 0) {
        return sessionReducer(touched, { type: 'remove-line', key: action.key }, now)
      }
      return {
        ...touched,
        lines: touched.lines.map((line) =>
          line.key === action.key
            ? { ...line, quantity: Math.min(action.quantity, MAX_QUANTITY) }
            : line,
        ),
      }
    }

    case 'remove-line': {
      const lines = touched.lines.filter((line) => line.key !== action.key)
      // An empty cart has nothing to check out, so send them back to the menu.
      return {
        ...touched,
        lines,
        screen: lines.length === 0 && touched.screen === 'cart' ? 'menu' : touched.screen,
      }
    }

    case 'apply-coupon':
      return { ...touched, couponCode: action.code?.trim().toUpperCase() || null }

    case 'order-placed':
      return {
        ...touched,
        screen: 'success',
        placedOrder: {
          orderNumber: action.orderNumber,
          statusToken: action.statusToken,
          estimatedWaitSeconds: action.estimatedWaitSeconds,
        },
      }

    case 'reset':
      return newSession(now)

    default:
      return touched
  }
}

/**
 * useReducer-compatible wrapper.
 *
 * `sessionReducer` takes an explicit clock so idle behaviour can be tested
 * deterministically; React's useReducer only passes two arguments, so this is
 * what the component binds to.
 */
export const kioskReducer = (state: KioskSession, action: KioskAction): KioskSession =>
  sessionReducer(state, action)

export interface KioskTotals {
  subtotal: Money
  vat: Money
  total: Money
  itemCount: number
}

export function sessionTotals(state: KioskSession, menu: MenuResponse | null): KioskTotals {
  if (!menu || state.lines.length === 0) {
    return { subtotal: 0, vat: 0, total: 0, itemCount: 0 }
  }

  const modifierPrices = new Map<string, number>()
  for (const product of menu.products) {
    for (const group of product.modifierGroups) {
      for (const option of group.options) modifierPrices.set(option.id, option.priceDelta)
    }
  }

  const totals = computeOrderTotals({
    lines: state.lines.map((line) => ({
      productId: line.product.id,
      variantId: line.variantId,
      unitPrice:
        line.product.variants.find((variant) => variant.id === line.variantId)?.price ??
        line.product.price,
      quantity: line.quantity,
      modifiers: line.modifierIds.map((id) => ({ id, priceDelta: modifierPrices.get(id) ?? 0 })),
    })),
    vat: { rateBp: menu.branch.vatRateBp, mode: menu.branch.vatMode },
  })

  return {
    subtotal: totals.subtotal,
    vat: totals.vat,
    total: totals.total,
    itemCount: state.lines.reduce((count, line) => count + line.quantity, 0),
  }
}

/** Unit price of whatever is on the customize screen, including modifiers. */
export function customizeUnitPrice(state: KioskSession): Money {
  const item = state.customizing
  if (!item) return 0

  const base =
    item.product.variants.find((variant) => variant.id === item.variantId)?.price ??
    item.product.price

  const deltas = item.product.modifierGroups
    .flatMap((group) => group.options)
    .filter((option) => item.modifierIds.includes(option.id))
    .reduce((sum, option) => sum + option.priceDelta, 0)

  return base + deltas
}

/**
 * FR-KIO-1 — idle handling.
 *
 * The attract screen never times out (there is nothing to lose), and neither
 * does the success screen — a customer reading their order number must not have
 * it snatched away mid-glance.
 */
export function idleStateFor(
  state: KioskSession,
  now: number,
  timeoutSec: number,
  warningSec = 10,
): 'active' | 'warning' | 'expired' {
  if (state.screen === 'attract' || state.screen === 'success') return 'active'

  const idleSec = (now - state.lastActivityAt) / 1000
  if (idleSec >= timeoutSec) return 'expired'
  if (idleSec >= timeoutSec - warningSec) return 'warning'
  return 'active'
}

export function secondsUntilReset(state: KioskSession, now: number, timeoutSec: number): number {
  return Math.max(0, Math.ceil(timeoutSec - (now - state.lastActivityAt) / 1000))
}
